<template>
  <interview-layout>
    <!-- inner wrapper used for centering -->
    <div class="container w-100 d-flex flex-column">

      <div class="submit-result-indicator">
        <div class="submit-message">
          <div class="text-success pulse-opacity-infinite" v-show="busy">
            <i class="bi bi-arrow-down-up me-2"></i>
            Resultaat opslaan...
          </div>
          <div class="text-success" v-show="resultSubmitted">
            <i class="bi bi-check-lg me-2"></i>
            Opgeslagen!
          </div>
          <div class="text-danger" v-show="!busy && !resultSubmitted">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            Er is iets fout gegaan!
            <span v-if="uiStore.uuid">
              U kunt proberen de pagina te
              <base-button
                variant="danger"
                class="ms-2 d-inline-block"
                icon="bi-arrow-clockwise"
                icon-position="start"
                @click="reloadPage">
                herladen
            </base-button>
            </span>
          </div>
        </div>
      </div>

      <!-- title above form card -->
      <h1 class="px-4 fade-in-up-enter-active animation-slowest max-100">
        <span :class="{'text-primary': overallStatus !== 'all_disqualified', 'text-danger': overallStatus == 'all_disqualified', }">
          {{mainTitle}}
        </span>
      </h1>
      <h5 class="px-4 pb-3 fade-in-up-enter-active animation-slowest delay-200 max-100" v-html="subTitle"></h5>

      <!-- result cards were here -->

      <!-- issues -->
      <Transition name="fade-in-up">
        <div class="issue-summaries" v-show="expandedIssueSummary !== ''">
          <template v-for="section in allSections" :key="section.key">
            <qual-issue-summary :section="section" :expand="expandedIssueSummary == section.key" @nav-to-issue="onNavigateToIssueView" :display-summary-descriptions="true" />
          </template>
        </div>
      </Transition>

      <div v-if="anyQualified && resultSubmitted" class="mx-n4 mx-sm-0 px-sm-4 w-sm-100">
        <base-card
          class="mx-auto mt-5 mb-n5 position-relative appointment-scheduler-bubble fade-in-up-enter-active animation-slowest delay-500"
          inner-class="appointment-scheduler-bubble-inner py-3"
          style="z-index: 3;"
          color="success"
          :opaque="true"
          :bubble-bottom-left="true"
        >
          <img src="@/assets/img/constantijn_blaauwhof.jpg" class="appointment-scheduler-bubble-avatar me-3" alt="Adviseur Constantijn Blaauwhof">
          <div class="d-flex flex-column">
            <h6 class="text-white mb-0 pb-0 fs-1-2em">Plan direct een afspraak met een adviseur!</h6>
            <span class="text-white fs-0-9em opacity-75">Onze vakkundige adviseurs weten alles over verduurzaming binnen Maatregel 29.</span>
          </div>
        </base-card>
        <base-card
          :stretch-inner="true"
          class="fade-in-up-enter-active animation-slowest delay-1300"
          inner-class="overflow-hidden calendly-brand-bg"
        >
          <appointment-scheduler class="mt-4" />
        </base-card>
      </div>
      
      <transition name="fade-in-up">
        <div class="px-4 mt-5 ms-auto" v-show="!busy || resultSubmitted">
          <return-to-portal-button v-if="lead.meta.ReturnUrl" text-align="end" />
        </div>
      </transition>

      <transition name="fade-in-up">
        <div class="call-request-container px-4 mt-5 ms-auto" v-show="!anyQualified && !lead.meta.ReturnUrl">
          <call-request-card />
        </div>
      </transition>

      <button-row class="mt-4">
        <base-button variant="minimal-secondary" icon="bi-arrow-left" icon-position="start" @click="onPrev">Terug</base-button>
      </button-row>
      <!-- inner wrapper end -->
    </div>
  </interview-layout>
</template>

<script>
  import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
  import BaseCard from '@/components/BaseCard.vue';
  import BaseButton from "@/components/BaseButton.vue";
  import ButtonRow from "@/components/ButtonRow.vue";
  import QualIssueSummary from "@/components/QualIssueSummary.vue";
  import CallRequestCard from "@/components/CallRequestCard.vue";
  import AppointmentScheduler from "@/components/AppointmentScheduler.vue";
  import ReturnToPortalButton from "@/components/ReturnToPortalButton.vue";

  import { useAllStores } from "@/helpers/UseAllStores";
  import { FlowService } from "@/services/FlowService";
  import { useQualificationStore, enabledSections } from '@/store/QualificationStore';
  import PushWithParams from "@/helpers/PushWithParams";
  import { StripQuery } from "@/helpers/StripQuery";

  import { filter } from 'lodash';
  import { LeadDataService } from "@/services/LeadDataService";

  export default {
    components: {
      InterviewLayout,
      BaseCard,
      BaseButton,
      ButtonRow,
      QualIssueSummary,
      CallRequestCard,
      AppointmentScheduler,
      ReturnToPortalButton
    },
    setup() {
      let qualStore = useQualificationStore();
      return {...useAllStores(), qualStore};
    },
    data() {
      return {
        expandedIssueSummary: '',
        busy: false,
        resultSubmitted: false,
        showAppointmentScheduler: false
      }
    },
    computed: {
      overallStatus() {
        return 'all_qualified';
      },
      anyQualified() {
        return (this.overallStatus == 'all_qualified' || this.overallStatus == 'partially_qualified');
      },
      mainTitle() {
        if (this.overallStatus == 'all_disqualified') {
          return 'We gaan uw situatie beoordelen';
        } else if (this.overallStatus == 'all_qualified' || this.overallStatus == 'partially_qualified') {
          return 'U wordt aangemeld';
        } else if (this.overallStatus == 'none_qualified_and_incomplete') {
          return 'Er ontbreekt nog informatie';
        }
        return 'Resultaten';
      },
      subTitle() {
        if (this.overallStatus == 'all_disqualified') {
          return 'Neem contact op en bespreek uw situatie met een adviseur.';
        } else if (this.overallStatus == 'all_qualified') {
          return 'voor ondersteuning bij het verduurzamen van uw woning.';
        } else if (this.overallStatus == 'partially_qualified') {
          return 'voor ondersteuning bij het verduurzamen van uw woning.';
        } else if (this.overallStatus == 'none_qualified_and_incomplete') {
          return 'We beoordelen uw aanvraag.';
        }
        return 'Beantwoord alle vragen of neem contact op!';
      },

      anySummaryExpanded() {
        return this.expandedIssueSummary !== '' && this.expandedIssueSummary !== ' ' && this.expandedIssueSummary;
      },

      allSections() { // all checked interests
        return enabledSections.value;
      },
      qualifiedSections() { // qualified sections only
        return filter(this.qualStore.sections, (section) => {
          return section.qualified == true;
        });
      }
    },
    methods: {
      toggleIssueSummary(sectionKey) {
        this.expandedIssueSummary = this.expandedIssueSummary == sectionKey ? '' : sectionKey;
      },
      otherSummaryExpanded(sectionKey) {
        return this.anySummaryExpanded && this.expandedIssueSummary !== sectionKey;
      },
      onNavigateToIssueView(viewName) {
        PushWithParams(
          this.$router,
          this.$route,
          {
            name: viewName,
            query: { 'from_result': 'true' }
          },
          true);
      },
      toggleAppointmentScheduler() {
        this.showAppointmentScheduler = !this.showAppointmentScheduler;
      },

      submitResult() {
        this.busy = true;
      
        LeadDataService.pushRemoteData(this.uiStore.uuid, true)
        .then(() => {
          this.resultSubmitted = true;
        })
        .catch(() => {
          this.resultSubmitted = false;
          console.log('Failed to submit result.');
        })
        .then(() => {
          this.busy = false;
        });
      },

      onPrev() {
        FlowService.pushPrev(this);
      },
      sectionOverviewCardColor(section) {
        if (section.status == 'qualified') {
          return 'success';
        } else if (section.status == 'disqualified') {
          return 'danger';
        } else {
          return 'secondary';
        }
      },
      reloadPage() {
        window.location.reload();
      }
    },
    beforeRouteEnter(to) {
      if ('from_result' in to.query) {
        to.query = StripQuery(to.query, 'from_result');
        return to;
      }
    },
    mounted() {
      this.submitResult();
    }
  }
</script>

<style scoped lang="scss">
.issue-summary-button-wrapper {
  font-size: 0.8em;
  text-align: center;
}
.overview-item {
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 1 !important;
  }
}

.submit-result-indicator {
  padding: 0 map-get($spacers, 4);
  margin-bottom: map-get($spacers, 2);
  opacity: 0.7;

  .submit-message {
    font-weight: 600;
    font-size: 0.8em;
    text-transform: uppercase;
  }
}
</style>

<style lang="scss">
  .calendly-brand-bg {
    background-color: #fbfcfd !important;
  }
  .appointment-scheduler-bubble {
    &-inner {
      display: flex;
      align-items: center;
      @include std-box-shadow(4, 10, map-get($theme-colors, 'success'), true);
    }
    &-avatar {
      border-radius: 99px;
      height: 50px;
    }
  }
</style>